import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  browserSessionPersistence,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut
} from 'firebase/auth'
import { initializeApp } from 'firebase/app'

const serializeFirebaseUser = (user) => {
  if (!user || typeof user !== 'object') return null

  return {
    uid: user.uid || '',
    displayName: user.displayName || '',
    email: user.email || '',
    photoURL: user.photoURL || ''
  }
}

const app = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || ''
})

const auth = getAuth(app)

/** Session-only: closing the browser ends Firebase auth (no long-lived tabs). */
const persistenceReady = setPersistence(auth, browserSessionPersistence).catch((err) => {
  console.error('Failed to set auth persistence:', err)
})

/**
 * Fresh Firebase ID token for API calls.
 * @param {boolean} [forceRefresh=false]
 * @returns {Promise<string>}
 */
export const getFreshIdToken = async (forceRefresh = false) => {
  await persistenceReady
  const user = auth.currentUser
  if (!user) {
    const err = new Error('Not signed in')
    err.code = 'auth/no-current-user'
    throw err
  }
  return user.getIdToken(forceRefresh)
}

export const getFirebaseAuth = () => auth

const signInWithGoogle = createAsyncThunk(
  'auth/signInWithgoogle',
  async (arg, { rejectWithValue }) => {
    const noop = () => {}
    const onSuccess = typeof arg === 'function'
      ? arg
      : arg?.onSuccess || noop

    const onFailure = typeof arg === 'object' && arg
      ? arg?.onFailure || noop
      : noop

    try {
      await persistenceReady
      const provider = new GoogleAuthProvider()
      const res = await signInWithPopup(auth, provider)
      const token = await res.user.getIdToken()

      await onSuccess()
      return { token, user: serializeFirebaseUser(res.user) }
    } catch (err) {
      console.error('Google sign-in failed:', {
        code: err?.code,
        message: err?.message,
        customData: err?.customData
      })
      await onFailure()
      return rejectWithValue(false)
    }
  }
)

const signOutUser = createAsyncThunk(
  'auth/signOutUser',
  async (_, { dispatch }) => {
    try {
      await signOut(auth)
    } catch (err) {
      console.error('Firebase sign-out failed:', err)
    } finally {
      dispatch(authSlice.actions.loggedOut())
    }
  }
)

/**
 * Wait for Firebase to finish restoring the session, then mirror into Redux.
 * Subscribe afterward for sign-in / sign-out while the app is open.
 */
const hydrateAuthSession = createAsyncThunk(
  'auth/hydrateAuthSession',
  async (_, { dispatch }) => {
    await persistenceReady

    if (typeof auth.authStateReady === 'function') {
      await auth.authStateReady()
    }

    const applyUser = async (user) => {
      if (!user) {
        dispatch(authSlice.actions.loggedOut())
        return { authenticated: false }
      }
      try {
        const token = await user.getIdToken(false)
        dispatch(authSlice.actions.sessionRestored({
          token,
          user: serializeFirebaseUser(user)
        }))
        return { authenticated: true }
      } catch (err) {
        console.error('Failed to restore auth session:', err)
        dispatch(authSlice.actions.loggedOut())
        return { authenticated: false }
      }
    }

    const initial = await applyUser(auth.currentUser)
    dispatch(authSlice.actions.setAuthReady(true))

    onAuthStateChanged(auth, (user) => {
      // Ignore until boot finished — authStateReady already applied initial state
      applyUser(user)
    })

    return initial
  }
)

/** Force-refresh ID token into Redux; used by HTTP 401 retry. */
const refreshAuthToken = createAsyncThunk(
  'auth/refreshAuthToken',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const token = await getFreshIdToken(true)
      dispatch(authSlice.actions.tokenUpdated(token))
      return token
    } catch (err) {
      await dispatch(signOutUser())
      return rejectWithValue(err?.message || 'Session expired')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    /** False until first Firebase auth event — gate protected routes. */
    ready: false,
    loading: false,
    isAuthenticated: false,
    token: null,
    user: null
  },
  reducers: {
    loggedOut: (state) => {
      state.token = null
      state.user = null
      state.loading = false
      state.isAuthenticated = false
      state.ready = true
    },
    setAuthReady: (state, action) => {
      state.ready = Boolean(action.payload)
    },
    sessionRestored: (state, action) => {
      state.token = action.payload?.token || null
      state.user = action.payload?.user || null
      state.isAuthenticated = Boolean(action.payload?.token)
      state.loading = false
      state.ready = true
    },
    tokenUpdated: (state, action) => {
      state.token = action.payload || null
      if (action.payload) state.isAuthenticated = true
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false
        state.token = action.payload?.token || null
        state.user = action.payload?.user || null
        state.isAuthenticated = true
        state.ready = true
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true
      })
      .addCase(signInWithGoogle.rejected, (state) => {
        state.loading = false
        state.token = null
        state.user = null
        state.isAuthenticated = false
        state.ready = true
      })
      .addCase(signOutUser.pending, (state) => {
        state.loading = true
      })
      .addCase(signOutUser.fulfilled, (state) => {
        state.token = null
        state.user = null
        state.loading = false
        state.isAuthenticated = false
        state.ready = true
      })
      .addCase(signOutUser.rejected, (state) => {
        state.token = null
        state.user = null
        state.loading = false
        state.isAuthenticated = false
        state.ready = true
      })
      .addCase(hydrateAuthSession.pending, (state) => {
        state.ready = false
      })
  }
})

const { loggedOut, setAuthReady, sessionRestored, tokenUpdated } = authSlice.actions

export const actions = {
  signInWithGoogle,
  signOutUser,
  hydrateAuthSession,
  refreshAuthToken,
  loggedOut,
  setAuthReady,
  sessionRestored,
  tokenUpdated
}
export const { reducer } = authSlice
